import { useState, useEffect, useRef, useCallback } from "react";

/**
 * A custom React hook to simulate a typewriter effect for text,
 * designed to work smoothly with incrementally updated (streaming) text.
 *
 * @param text The full string that should be "typed out" progressively. This string is expected to grow over time.
 * @param speed The delay in milliseconds between each character being displayed.
 * @returns An object containing:
 * - displayedText: The progressively revealed string, character by character.
 * - isTypingComplete: A boolean indicating if the entire 'text' has been displayed.
 */
export const useTypewriterEffect = (text: string, speed = 30) => {
  const [displayedText, setDisplayedText] = useState("");
  const [isTypingComplete, setIsTypingComplete] = useState(false);

  const currentTextIndex = useRef(0);
  const animationFrameIdRef = useRef<number | null>(null);
  const lastCharTimeRef = useRef(performance.now());
  const prevTextPropValueRef = useRef("");

  const animate = useCallback(
    (currentTime: DOMHighResTimeStamp) => {
      const deltaTime = currentTime - lastCharTimeRef.current;

      if (currentTextIndex.current < text.length && deltaTime >= speed) {
        setDisplayedText(
          (prev) => prev + text.charAt(currentTextIndex.current),
        );
        currentTextIndex.current++;
        lastCharTimeRef.current = currentTime;
      }

      if (currentTextIndex.current < text.length) {
        animationFrameIdRef.current = requestAnimationFrame(animate);
        setIsTypingComplete(false);
      } else {
        setDisplayedText(text);
        setIsTypingComplete(true);
      }
    },
    [
      text,
      speed,
      lastCharTimeRef,
      currentTextIndex,
      animationFrameIdRef,
      setDisplayedText,
      setIsTypingComplete,
    ],
  );

  useEffect(() => {
    if (animationFrameIdRef.current !== null) {
      cancelAnimationFrame(animationFrameIdRef.current);
      animationFrameIdRef.current = null;
    }

    const isNewFullMessage =
      !text || !text.startsWith(prevTextPropValueRef.current);

    if (isNewFullMessage) {
      setDisplayedText("");
      currentTextIndex.current = 0;
      lastCharTimeRef.current = performance.now();
      setIsTypingComplete(false);
    } else {
      currentTextIndex.current = displayedText.length;
    }

    prevTextPropValueRef.current = text;

    if (!text) {
      setDisplayedText("");
      setIsTypingComplete(true);
      return;
    }

    if (currentTextIndex.current < text.length) {
      animationFrameIdRef.current = requestAnimationFrame(animate);
    } else {
      setDisplayedText(text);
      setIsTypingComplete(true);
    }

    return () => {
      if (animationFrameIdRef.current !== null) {
        cancelAnimationFrame(animationFrameIdRef.current);
      }
    };
  }, [text, speed, displayedText.length, animate]);

  return { displayedText, isTypingComplete };
};
